import { Box, Stack, Typography } from '@mui/material'
import React, { Ref, RefObject, useCallback, useMemo, useState } from 'react'
import { Button, InformationModal } from 'utoniq-ui'

import {
  GachaOrderDetailFragment,
  GachaResultQuery,
  GachaRollType,
  GachaTicketConnectionFragment,
  Maybe,
  PortfolioConnectionFragment,
  ProtectUserItemInput,
} from '@/clients/utoniq-core/schema'
import { BaseLayout, Head } from '@/components/layouts'
import {
  FullScreenAnimationCell,
  VideoFullScreen,
  VideoFullScreenRef,
} from '@/components/lib/animation'
import {
  ConfirmationRollGachaByPointModal,
  ConfirmationRollGachaByTicketModal,
  HeaderDisplay,
  InsufficientGachaTicketModal,
  LoadingWithBackdrop,
  PointHeaderDisplay,
} from '@/components/organisms'
import { Loading } from '@/components/organisms/loading'
import { ArtistThemeProvider } from '@/components/styles'
import { config } from '@/config'
import { User, useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'
import { individualHideGachaRemainingItemCount } from '@/core/individual'

import { SkipButton } from '../../GachaDetail/template/components/SkipButton'
import { StandbyTrigger } from '../../GachaDetail/template/components/StandbyTrigger'
import { OwnedItemDetailModal } from './components/OwnedItemDetailModal'
import { SingleGachaResultCell } from './SingleGachaResult'
import { TenGachaResultCell } from './TenGachaResult'

export * from './fragments'

export type State = {
  loading: boolean
  gachaRolling: boolean
  standingBy: boolean
  playingAnimation: boolean
  apngAnimationPlaying: boolean
  protectingItem: boolean
  user: Maybe<GachaResultQuery['user']>
  loggedUser: Maybe<User>
  selectedItem: Maybe<PortfolioConnectionFragment>
  selectingItem: boolean
  gachaOrder: Maybe<GachaOrderDetailFragment>
  gachaTicket: Maybe<GachaTicketConnectionFragment>
  ticket: Maybe<number>
  soldOutModalInfo: {
    open: boolean
    onClose: () => void
  }
  videoFullscreenRef: RefObject<VideoFullScreenRef>
  standbyVideoFullscreenRef: RefObject<VideoFullScreenRef>
  apngFullScreenRef: Ref<HTMLCanvasElement>
  prepaidPointBalance: number
  formattedPrepaidPointBarance: string
  isSafari: boolean
  QRCodePrinting: boolean
}

export type Action = {
  rollGacha: (count: number, rollType: GachaRollType) => Promise<void>
  rollGachaAfterStandingby: () => void
  onSelectedItem: (itemId: string) => Promise<void>
  onClickShare: (portfolio: Maybe<PortfolioConnectionFragment>) => void
  onSettingWallpaper: (itemId?: string) => Promise<boolean>
  onProtectUserItem: (values: ProtectUserItemInput) => Promise<void>
  onImageDownload: (url?: string) => Promise<void>
  onInitializeStandbyAnimation: (count: number) => void
  onSkip: () => void
  onUseTicket: (
    itemId: string | undefined,
    serialIds: string[],
    throughTicketName?: string
  ) => Promise<void>
  onSettingAvatar: (itemId?: string) => Promise<void>
  onGenerateWallpaperPrintQrcode: (serialId: string) => Promise<void>
}

export const GachaResultTemplate: Template<State, Action> = ({
  state,
  rollGacha,
  rollGachaAfterStandingby,
  onSelectedItem,
  onClickShare,
  onSettingWallpaper,
  onSkip,
  onProtectUserItem,
  onImageDownload,
  onUseTicket,
  onSettingAvatar,
  onInitializeStandbyAnimation,
  onGenerateWallpaperPrintQrcode,
}) => {
  const { t } = useTranslation('gachaResult')
  const { routerNames } = useRouter<{ gachaId: string; gachaOrderId: string }>()
  const [open, setOpen] = useState<boolean>(false)
  const [skipDisplayAnimation, setSkipDisplayAnimation] = useState(false)
  const [count, setCount] = useState<number>(0)
  const [openConfirm, setOpenConfirm] = useState<boolean>(false)
  const [openConfirmByPoint, setOpenConfirmByPoint] = useState<boolean>(false)
  const [openInsufficientGachaTicket, setOpenInsufficientGachaTicket] =
    useState(false)

  /**
   * rebuild rewardItems from count inside rewardItem
   * ex: length of rewardItems = 1 and rewardItem.count = 3 => length of rewardItems = 3
   * https://app.clickup.com/t/908127/UT-3865
   */
  const rewardItems = useMemo<GachaOrderDetailFragment['rewardItems']>(() => {
    if (!state.gachaOrder?.rewardItems) return []

    const rewardItems: GachaOrderDetailFragment['rewardItems'] = []
    state.gachaOrder.rewardItems.map(rewardItem => {
      rewardItems.push(
        ...Array.from({ length: rewardItem.count }, () => rewardItem)
      )
    })
    return rewardItems
  }, [state.gachaOrder?.rewardItems])

  const title = useMemo(() => {
    if (!rewardItems) return ''

    if (rewardItems.length === 1) return routerNames.SingleGachaResult

    return routerNames.TenGachaResult
  }, [routerNames.SingleGachaResult, routerNames.TenGachaResult, rewardItems])

  const onClose = useCallback(() => {
    setOpen(false)
  }, [])

  const onSelectUserItem = useCallback(
    (itemId: string) => {
      setOpen(true)
      onSelectedItem(itemId)
    },
    [onSelectedItem]
  )

  const isPointOnly =
    state.gachaOrder?.gacha?.gachaRollType === GachaRollType.PrepaidPoint
  const hasPriceGacha = !!state.gachaOrder?.gacha?.price

  if (state.apngAnimationPlaying)
    return (
      <>
        <FullScreenAnimationCell ref={state.apngFullScreenRef} />
        <SkipButton
          onSkip={() => {
            setOpenConfirm(false)
            setOpenConfirmByPoint(false)
            setOpenInsufficientGachaTicket(false)
            onSkip()
          }}
        />
      </>
    )

  return (
    <ArtistThemeProvider artistId={state.gachaOrder?.gacha.artistId}>
      <BaseLayout
        header={{
          back: true,
          title: t('title'),
          right: state.gachaOrder && (
            <>
              {state.gachaOrder.gacha.gachaRollType ===
              GachaRollType.GachaTicket ? (
                <HeaderDisplay
                  src={state.gachaTicket?.thumbnails?.standard?.smallUri || ''}
                  ticket={state.ticket ?? ''}
                />
              ) : (
                <PointHeaderDisplay
                  point={state.formattedPrepaidPointBarance}
                />
              )}
            </>
          ),
        }}
        sx={{ height: '100dvh' }}
      >
        <Head title={title ? title : routerNames.SingleGachaResult} />
        <Box
          id="result-root"
          sx={{
            height: '100%',
            maxHeight: 812,
            display: 'flex',
            flexDirection: 'column',
            '& > *:first-of-type': {
              flex: 1,
            },
            overflow: rewardItems.length === 1 ? 'hidden' : 'unset',
          }}
        >
          {!rewardItems.length && state.loading && (
            <Box
              id="loading"
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Loading />
            </Box>
          )}
          {rewardItems.length === 1 && (
            <SingleGachaResultCell
              rewardItem={rewardItems[0]}
              artistId={state.gachaOrder?.gacha.artistId}
              onSelectUserItem={onSelectUserItem}
              rollGacha={count => {
                setCount(count)
                onInitializeStandbyAnimation(count)

                if (!isPointOnly) {
                  /**
                   * チケット・ポイント両方で引けるガチャ且つ、チケットが足りない場合、ポイントで引くモーダルを出す
                   */
                  if (hasPriceGacha && count > (state.ticket ?? 0)) {
                    setOpenInsufficientGachaTicket(true)

                    return
                  }

                  /**
                   * ガチャチケのみ許容のガチャの時、チケットで引くモーダルを出す
                   */
                  setOpenConfirm(true)

                  return
                }

                /**
                 * ポイント専用ガチャの時、ポイントで引くモーダルを出す
                 */
                setOpenConfirmByPoint(true)
              }}
              loading={state.gachaRolling}
              ticket={state.ticket}
              gacha={state.gachaOrder?.gacha}
              isPointOnly={isPointOnly}
              prepaidPointBalance={state.prepaidPointBalance}
            />
          )}
          {rewardItems.length > 1 && (
            <TenGachaResultCell
              loading={state.gachaRolling}
              artistId={state.gachaOrder?.gacha.artistId}
              rewardItems={rewardItems}
              ticket={state.ticket}
              gacha={state.gachaOrder?.gacha}
              skipAnimation={skipDisplayAnimation}
              onSelectUserItem={onSelectUserItem}
              setSkipDisplayAnimation={setSkipDisplayAnimation}
              rollGacha={count => {
                setCount(count)
                onInitializeStandbyAnimation(count)

                if (!isPointOnly) {
                  /**
                   * チケット・ポイント両方で引けるガチャ且つ、チケットが足りない場合、ポイントで引くモーダルを出す
                   */
                  if (hasPriceGacha && count > (state.ticket ?? 0)) {
                    setOpenInsufficientGachaTicket(true)

                    return
                  }

                  /**
                   * ガチャチケのみ許容のガチャの時、チケットで引くモーダルを出す
                   */
                  setOpenConfirm(true)

                  return
                }

                /**
                 * ポイント専用ガチャの時、ポイントで引くモーダルを出す
                 */
                setOpenConfirmByPoint(true)
              }}
              isPointOnly={isPointOnly}
              prepaidPointBalance={state.prepaidPointBalance}
            />
          )}
        </Box>

        {/**
         * ガチャを引く決定モーダル（チケット・ポイント）
         */}
        <ConfirmationRollGachaByTicketModal
          open={openConfirm}
          onClose={() => setOpenConfirm(false)}
          count={count}
          onClick={async () => {
            await rollGacha(count, GachaRollType.GachaTicket)
            setOpenConfirm(false)
            setSkipDisplayAnimation(false)
          }}
          gachaRolling={state.gachaRolling}
        />
        <ConfirmationRollGachaByPointModal
          open={openConfirmByPoint}
          onClose={() => setOpenConfirmByPoint(false)}
          count={count}
          price={(state.gachaOrder?.gacha?.price ?? 0) * count}
          onClick={async () => {
            await rollGacha(count, GachaRollType.PrepaidPoint)
            setOpenConfirmByPoint(false)
            setSkipDisplayAnimation(false)
          }}
          gachaRolling={state.gachaRolling}
        />

        {/**
         * ガチャチケ・ポイント量対応のガチャで、ガチャチケ不足時にポイントでガチャを引けるモーダル
         */}
        <InsufficientGachaTicketModal
          open={openInsufficientGachaTicket}
          onClose={() => setOpenInsufficientGachaTicket(false)}
          point={(state.gachaOrder?.gacha?.price ?? 0) * count}
          prepaidPointBalance={state.prepaidPointBalance}
          onClick={async () => {
            await rollGacha(count, GachaRollType.PrepaidPoint)
            setOpenConfirm(false)
            setSkipDisplayAnimation(false)
            setOpenInsufficientGachaTicket(false)
          }}
          gachaRolling={state.gachaRolling}
        />

        <GachaDrawErrorModal
          open={state.soldOutModalInfo?.open}
          onClose={state.soldOutModalInfo?.onClose}
        />

        {state.user && (
          <OwnedItemDetailModal
            open={open}
            protectingItem={state.protectingItem}
            portfolio={state.selectedItem}
            user={state.user}
            loggedUser={state.loggedUser}
            loading={state.selectingItem}
            onClose={onClose}
            QRCodePrinting={state.QRCodePrinting}
            onClickShare={async () => onClickShare(state.selectedItem)}
            onSettingWallpaper={() =>
              onSettingWallpaper(state.selectedItem?.item.id)
            }
            onProtectUserItem={() =>
              onProtectUserItem({
                id: state.selectedItem?.item.id ?? '',
                isUserProtected: !state.selectedItem?.isUserProtected,
              })
            }
            onSettingAvatar={() => onSettingAvatar(state.selectedItem?.item.id)}
            onUseTicket={(serialIds, throughTicketName) =>
              onUseTicket(
                state.selectedItem?.item.id,
                serialIds,
                throughTicketName
              )
            }
            onImageDownload={onImageDownload}
            onGenerateWallpaperPrintQrcode={onGenerateWallpaperPrintQrcode}
          />
        )}

        {/**
         *  ガチャアニメーション用のVideoFullScreen要素
         */}
        <VideoFullScreen
          ref={state.videoFullscreenRef}
          shouldMute={
            state.isSafari && !state.gachaOrder?.gacha.standByAnimations
          }
          sx={{ zIndex: 11 }}
        />
        {/**
         * スタンバイ動画用のVideoFullScreen要素
         */}
        {!!state.gachaOrder?.gacha.standByAnimations && (
          <VideoFullScreen
            ref={state.standbyVideoFullscreenRef}
            shouldMute={false}
            sx={{ zIndex: 10 }}
          />
        )}
        {/**
         * アニメーション動画を再生させるためのトリガー
         */}
        {!!state.gachaOrder?.gacha.standByAnimations && state.standingBy && (
          <StandbyTrigger onPlay={rollGachaAfterStandingby} />
        )}
        {/**
         * 動画スキップボタン
         */}
        {state.playingAnimation && <SkipButton onSkip={onSkip} />}

        {/**
         * ローディングインジケータを最前面で表示する
         */}
        <LoadingWithBackdrop open={state.gachaRolling} />
      </BaseLayout>
    </ArtistThemeProvider>
  )
}

type ButtonSetCellProps = {
  loading: boolean
  count: number
  ticket: State['ticket']
  rollGacha: (count: number) => void
  artistId?: string
  remainingLimitedItemCount: number
  totalLimitedItemCount: number
  isLimitedGacha: boolean
  isPointOnly: boolean
  price: number
  prepaidPointBalance: number
}
export const ButtonSetCell: Cell<ButtonSetCellProps> = ({
  loading,
  rollGacha,
  count,
  ticket,
  artistId,
  remainingLimitedItemCount,
  totalLimitedItemCount,
  isLimitedGacha,
  isPointOnly,
  price,
  prepaidPointBalance,
}) => {
  const { t } = useTranslation('gachaResult')
  const { router, routerPages, generatePath } = useRouter()

  const disabledRollGacha = useMemo(() => {
    // 在庫不足
    const isInventoryShortage =
      isLimitedGacha && remainingLimitedItemCount < count
    // 回してる途中
    if (loading) {
      return true
    }
    // 限定ガチャで在庫不足の場合
    if (isInventoryShortage) {
      return true
    }
    // in case roll gacha only by point
    if (isPointOnly) {
      // there are not enough points
      return price * count > prepaidPointBalance
    }

    // ガチャチケのみ許容のガチャで、チケットが足りないとき
    if (!price && count > (ticket ?? 0)) return true

    // in case roll gacha by point or ticket

    // there are still enough tickets
    if (count <= (ticket ?? 0)) {
      return false
    }
    // there are not enough points
    return price * count > prepaidPointBalance
  }, [
    count,
    isLimitedGacha,
    isPointOnly,
    loading,
    prepaidPointBalance,
    price,
    remainingLimitedItemCount,
    ticket,
  ])

  // 有限ガチャの場合の残り割合
  const textColor = disabledRollGacha
    ? 'text.hint'
    : remainingLimitedItemCount / totalLimitedItemCount < 0.2
    ? 'error.main'
    : 'text.primary'

  return (
    <CellFrame>
      <Stack spacing={4} sx={{ py: 5 }} direction="row" alignItems="end">
        <Button
          size="large"
          variant="outlined"
          fullWidth
          onClick={() =>
            router.push(
              generatePath(routerPages.ArtistCollections, {
                artistId: artistId ?? config.artistId,
              })
            )
          }
        >
          {t('toCollections')}
        </Button>

        {/**
         * 限定ガチャの残り個数表示
         */}
        <Stack sx={{ width: '100%' }}>
          {isLimitedGacha && !individualHideGachaRemainingItemCount() && (
            <Stack
              flexDirection="row"
              justifyContent="center"
              sx={{ width: '100%', pb: 2 }}
            >
              <Typography variant="overline" color={textColor}>
                {t('remaining', '残り')}{' '}
              </Typography>
              <Typography variant="overline" color={textColor}>
                {remainingLimitedItemCount} / {totalLimitedItemCount}{' '}
              </Typography>
              <Typography variant="overline" color={textColor}>
                {t('remainUnit', '個')}
              </Typography>
            </Stack>
          )}

          <Button
            size="large"
            variant="contained"
            fullWidth
            onClick={() => rollGacha(count)}
            disabled={disabledRollGacha}
          >
            {t('turnAgain')}
          </Button>
        </Stack>
      </Stack>
    </CellFrame>
  )
}

type GachaDrawErrorModalProps = {
  open: boolean
  onClose: () => void
}
const GachaDrawErrorModal = ({ open, onClose }: GachaDrawErrorModalProps) => {
  const { t } = useTranslation('gachaResult')

  return (
    <InformationModal
      open={open}
      onClose={onClose}
      title={t('gachaDrawErrorModalTitle')}
      content={t('gachaDrawErrorModalContent')}
      buttons={
        <Button
          sx={{ minWidth: 212 }}
          variant="contained"
          color="primary"
          size="large"
          onClick={onClose}
        >
          {t('close')}
        </Button>
      }
    />
  )
}
